<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { FIELD_CLASS } from '../constants';
import { emptyToNull } from '../helpers';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

// A click-to-drop-a-pin map is on hold pending a Leaflet/OpenStreetMap
// integration decision — latitude/longitude stay plain number inputs.
//
// `region` is free text on the API (FE handoff §4.1: "region free text;
// dropdown = GET /properties/regions") — there's no separate "create a
// region" endpoint, so this is just a text input with a <datalist> of
// regions already in use, not the old dropdown + "+ Add new region" flow
// (which existed only because the FE-only prototype held its region list
// client-side).
export default {
  name: 'LocationModal',
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: {
        area: this.project.area || '',
        address: this.project.address || '',
        region: this.project.region || '',
        latitude: this.project.latitude,
        longitude: this.project.longitude,
      },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', { regions: 'getRegions' }),
  },
  mounted() {
    this.fetchRegions();
  },
  methods: {
    ...mapActions('realEstate', ['patchProject', 'fetchRegions']),
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        await this.patchProject({
          propertyId: this.project.property_id,
          fields: {
            area: this.form.area.trim(),
            address: this.form.address.trim(),
            region: this.form.region.trim(),
            latitude: emptyToNull(this.form.latitude),
            longitude: emptyToNull(this.form.longitude),
          },
        });
        useAlert(this.$t('REAL_ESTATE.LOCATION.SUCCESS_UPDATED'));
        this.$emit('close');
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.LOCATION.ERROR_UPDATE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.LOCATION.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.LOCATION.AREA') }}
          </label>
          <input
            v-model="form.area"
            :placeholder="$t('REAL_ESTATE.LOCATION.AREA_PLACEHOLDER')"
            type="text"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.LOCATION.ADDRESS') }}
          </label>
          <input
            v-model="form.address"
            :placeholder="$t('REAL_ESTATE.LOCATION.ADDRESS_PLACEHOLDER')"
            type="text"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.LOCATION.LOCATION_LABEL') }}
          </label>
          <input
            v-model="form.region"
            type="text"
            list="realEstateRegionSuggestions"
            :placeholder="$t('REAL_ESTATE.LOCATION.REGION_PLACEHOLDER')"
            :class="fieldClass"
          />
          <datalist id="realEstateRegionSuggestions">
            <option v-for="region in regions" :key="region" :value="region" />
          </datalist>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.LOCATION.LATITUDE') }}
            </label>
            <input
              v-model.number="form.latitude"
              :placeholder="$t('REAL_ESTATE.LOCATION.LATITUDE_PLACEHOLDER')"
              type="number"
              step="any"
              :class="fieldClass"
            />
          </div>
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.LOCATION.LONGITUDE') }}
            </label>
            <input
              v-model.number="form.longitude"
              :placeholder="$t('REAL_ESTATE.LOCATION.LONGITUDE_PLACEHOLDER')"
              type="number"
              step="any"
              :class="fieldClass"
            />
          </div>
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.LOCATION.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.LOCATION.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
